import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Building2,
  Briefcase,
  Mic2,
  Sparkles,
  Scale,
  Vote,
  Wallet,
  Film,
  Lock,
  Compass,
  Users,
} from "lucide-react";
import PageHero from "@/components/site/PageHero";
import SectionLabel from "@/components/site/SectionLabel";
import Seo from "@/components/site/Seo";
import { ASSETS, BRAND } from "@/lib/constants";

// Distilled from the company's Foundational Business Charter (v1.0, June 2, 2026)
// and the Initial Founder Resolution. Public-facing summaries only — the formal
// documents remain the source of truth.
const PRINCIPLES = [
  {
    icon: Compass,
    title: "Founder-Led",
    body: "Single-member LLC, wholly owned and operated by R. Dale Brunet. Strategic direction, brand authority, and final decisions sit with the founder until formally restructured.",
  },
  {
    icon: Wallet,
    title: "Green-Light Financial Discipline",
    body: "An event is only formally green-lit once fixed costs are reasonably covered by written sponsor commitments, deposits, advance ticket revenue, or other reliable funding. We do not overextend before the support structure is in place.",
  },
  {
    icon: Users,
    title: "Team-Built, Founder-Aligned",
    body: "Each event is developed by a coordinated team of individuals, volunteers, and team members — each with specific tasking and goals aligned to producing the most professional and impactful show possible. Roles are defined; expectations are clear; the work is shared.",
  },
  {
    icon: Vote,
    title: "Contest & Audience Integrity",
    body: "A clear separation between sponsorship, contestant selection, production, and winner selection. Winners are chosen by an audience-participation scoring model designed to be transparent and auditable. No pay-for-play.",
  },
  {
    icon: ShieldCheck,
    title: "Sponsor Integrity",
    body: "Sponsorship creates visibility, alignment, and goodwill — never control over judging, contestants, awards, or speaker content. The producer reserves the right to decline sponsorships that conflict with the event's purpose.",
  },
  {
    icon: Film,
    title: "Media-First Approach",
    body: "Each event is built to live beyond its night. Recordings, recap reels, interviews, and post-event content are produced and monetized under proper releases — extending sponsor value and contestant exposure long after the lights come down.",
  },
  {
    icon: Lock,
    title: "Intellectual Property Ownership",
    body: "Brand marks, event formats, scoring systems, application materials, presentation decks, scripts, and operating procedures are assets of TNS Media Group. Contribution by advisors, vendors, or sponsors does not create ownership unless documented in writing.",
  },
];

const FUTURE_PROPERTIES = [
  "The Next Stage Live",
  "The Next Stage Speaker Showcase",
  "The Next Stage Awards",
  "The Next Stage Studios",
  "The Next Stage Podcast",
  "The Next Stage Academy",
  "The Next Stage Talent Network",
  "The Next Stage Media Network",
  "The Next Stage National Tour",
  "The Next Stage Regional Showcase Series",
];

export default function MediaGroup() {
  return (
    <div>
      <Seo />

      {/* 1. HERO */}
      <PageHero
        pageTitle="About The Next Stage Media Group"
        eyebrowNumber="01"
        eyebrow="The Operating Company Behind The Next Stage"
        title="The Next Stage"
        highlight="Media Group, LLC"
        tagline="A founder-led production, management, media, and rights-holding company built to identify, develop, and amplify emerging voices."
        description="The Next Stage Media Group, LLC is the operating company that produces, owns, and manages The Next Stage — and the platform from which future live events, media properties, and branded productions will be developed."
      />

      {/* 2. TROPHY MARK BAND */}
      <section className="bg-midnight border-t border-slate-soft relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"
        />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 flex flex-col items-center text-center relative">
          <img
            src={ASSETS.producerMark}
            alt={`${BRAND.producer} — trophy mark`}
            data-testid="mg-trophy-mark"
            className="w-full max-w-[260px] sm:max-w-[300px] h-auto object-contain drop-shadow-[0_18px_45px_rgba(229,178,93,0.25)]"
            loading="lazy"
          />
          <div className="font-bebas tracking-[0.32em] text-gold text-sm uppercase mt-8">
            {BRAND.producer}
          </div>
          <p className="font-bebas tracking-[0.32em] text-muted-ink mt-3 text-xs uppercase">
            Production · Management · Media · Rights
          </p>
        </div>
      </section>

      {/* 3. WHO WE ARE */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel number="02">Who We Are</SectionLabel>
            <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95]">
              A single-member company
              <br />
              <span className="gold-text">with a clear mission.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="font-manrope text-soft-gray text-lg leading-relaxed">
              The Next Stage Media Group, LLC is a single-member limited
              liability company wholly owned and operated by{" "}
              <strong className="text-white">R. Dale Brunet</strong>. Based in{" "}
              {BRAND.city}, the company exists as the operational, production,
              management, media, and rights-holding entity behind The Next Stage
              and any future properties developed under the company.
            </p>

            <div
              data-testid="mg-id-card"
              className="grid sm:grid-cols-2 gap-4 mt-8"
            >
              <Fact label="Entity">The Next Stage Media Group, LLC</Fact>
              <Fact label="Structure">Single-Member LLC</Fact>
              <Fact label="Founder / Owner">R. Dale Brunet</Fact>
              <Fact label="Headquarters">{BRAND.city}</Fact>
              <Fact label="Primary Property">The Next Stage</Fact>
              <Fact label="Founded">2026</Fact>
            </div>
          </div>
        </div>
      </section>

      {/* 4. WHY WE EXIST */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="03">Why We Exist</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] max-w-4xl">
            Identify. Develop.{" "}
            <span className="gold-text">Amplify.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-6 max-w-4xl leading-relaxed text-lg">
            The Next Stage Media Group exists to identify, develop, produce,
            and amplify emerging voices — speakers, entrepreneurs, storytellers,
            creators, advocates, educators, and public-facing leaders — through
            live events, media production, branded content, public speaking
            platforms, audience-engagement models, educational programming, and
            strategic business development.
          </p>

          <div
            data-testid="mg-pillars"
            className="grid sm:grid-cols-3 gap-6 mt-12"
          >
            <Pillar
              icon={Mic2}
              title="Live Events"
              body="Producing professional live experiences that move speakers from preparation to public presentation."
            />
            <Pillar
              icon={Film}
              title="Media Production"
              body="Capturing, editing, and distributing event media to extend reach long past the night of the show."
            />
            <Pillar
              icon={Briefcase}
              title="Business Development"
              body="Building sponsorships, strategic partnerships, talent networks, and the commercial scaffolding the platform needs to grow."
            />
          </div>

          <div className="corner-bracket card-dark p-7 mt-10 max-w-4xl">
            <p className="font-cormorant italic text-2xl lg:text-3xl text-gold-bright leading-snug">
              &ldquo;The company is the platform, owner, producer, manager, and
              rights holder. The event is the first of what we will build.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* 5. THE COMPANY VS. THE EVENT */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="04">Company vs. Event</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] max-w-3xl">
            The company is not the event.
            <br />
            <span className="gold-text">The company produces it.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-6 max-w-3xl leading-relaxed">
            The distinction matters. The Next Stage Media Group, LLC owns and
            produces The Next Stage — and may develop, own, license, or
            discontinue related brands, marks, and properties at its discretion.
            The Next Stage is a branded property of the company; it is not
            currently a separate legal entity.
          </p>

          <div
            data-testid="mg-architecture"
            className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 mt-12 items-stretch"
          >
            <ArchCard
              kicker="Parent / Operating Entity"
              title="The Next Stage Media Group, LLC"
              body="The production, management, media, and rights-holding company. Owns the brand. Signs the contracts. Holds the IP."
              icon={Building2}
              featured
            />
            <div className="hidden lg:flex items-center justify-center">
              <div className="font-bebas text-gold tracking-[0.32em] text-sm">
                OWNS →
              </div>
            </div>
            <ArchCard
              kicker="Primary Event / Show Property"
              title="The Next Stage"
              body="The first live event and media property. Public speaking competition and showcase. Audience-judged. Founder-led production."
              icon={Mic2}
            />
          </div>

          <div className="mt-12">
            <div className="label-bebas text-gold">
              Future Brand Extensions Under Exploration
            </div>
            <p className="font-manrope text-muted-ink text-sm mt-2 max-w-3xl leading-relaxed">
              The following are exploratory brand names under the company. They
              are not separate entities unless and until formally established.
              The company may create, license, sell, or discontinue any of these
              at its discretion.
            </p>
            <ul
              data-testid="mg-future-properties"
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 mt-6"
            >
              {FUTURE_PROPERTIES.map((p) => (
                <li
                  key={p}
                  className="font-manrope text-soft-gray text-sm flex items-start gap-2"
                >
                  <span className="text-gold mt-1">·</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 6. ROLE ON THE NEXT STAGE */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="05">
            Our Role On The Next Stage
          </SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] max-w-3xl">
            Owner. Producer.
            <br />
            <span className="gold-text">Manager. Rights Holder.</span>
          </h2>

          <div
            data-testid="mg-role-grid"
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10"
          >
            <Role
              num="01"
              title="Owner"
              body="The brand, format, scoring systems, and event IP are wholly owned by the company."
            />
            <Role
              num="02"
              title="Producer"
              body="Venue, stage, lighting, sound, run-of-show, contestant experience, and the live production are managed by the company."
            />
            <Role
              num="03"
              title="Manager"
              body="Sponsorship, applications, audience engagement, vendor relationships, and event-day operations sit under the company's control."
            />
            <Role
              num="04"
              title="Rights Holder"
              body="All photo, video, audio, and post-event content rights are held by the company, subject to participant releases and applicable law."
            />
          </div>

          <div className="mt-10 corner-bracket card-dark p-7 max-w-4xl">
            <div className="label-bebas text-gold mb-2">Important</div>
            <p className="font-manrope text-soft-gray leading-relaxed">
              Participation by any person or organization in conversations,
              advisory meetings, planning sessions, sponsorship discussions,
              production activities, or volunteer efforts shall not create
              ownership, equity, partnership, founder status, employment, or
              decision-making authority unless expressly documented in a signed
              written agreement.
            </p>
          </div>
        </div>
      </section>

      {/* 7. OPERATING PRINCIPLES */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="06">Operating Principles</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] max-w-3xl">
            How <span className="gold-text">we operate.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-4 max-w-3xl leading-relaxed">
            Summarized from the company&rsquo;s Foundational Business Charter
            and Initial Founder Resolution. The formal documents are the source
            of truth.
          </p>
          <div
            data-testid="mg-principles-grid"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10"
          >
            {PRINCIPLES.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`mg-principle-${p.title
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")}`}
                  className="card-dark p-7"
                >
                  <Icon className="text-gold" size={28} strokeWidth={1.6} />
                  <h3 className="font-shoulders font-black uppercase tracking-wider text-xl text-white mt-5">
                    {p.title}
                  </h3>
                  <p className="font-manrope text-soft-gray text-[15px] mt-3 leading-relaxed">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8. FOUNDER */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <img
              src={ASSETS.founderHeadshot}
              alt={BRAND.founder}
              data-testid="mg-founder-photo"
              className="w-full max-w-[280px] mx-auto object-contain"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-7">
            <SectionLabel number="07">Founder &amp; Owner</SectionLabel>
            <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95]">
              {BRAND.founder}
            </h2>
            <p className="font-bebas tracking-[0.22em] text-gold text-sm mt-3 uppercase">
              {BRAND.founderTitle} · {BRAND.producer}
            </p>
            <p className="font-manrope text-soft-gray mt-5 leading-relaxed max-w-2xl">
              R. Dale Brunet is the founder, sole member, owner, and authorized
              representative of The Next Stage Media Group, LLC. He retains full
              authority over company direction, event development, intellectual
              property, brand usage, financial commitments, sponsorships,
              partnerships, contracts, and media rights — and is the source of
              every signed commitment the company makes.
            </p>
            <Link
              to="/about#about-founder"
              data-testid="mg-about-founder-link"
              className="btn-outline mt-7"
            >
              Read More About R. Dale Brunet
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* 9. CONTACT / CTA */}
      <section className="bg-navy border-t border-slate-soft relative overflow-hidden">
        <img
          src={ASSETS.producerMark}
          alt=""
          aria-hidden="true"
          data-testid="mg-contact-watermark"
          className="hidden sm:block absolute -right-12 top-1/2 -translate-y-1/2 w-[420px] h-auto opacity-[0.06] pointer-events-none select-none"
          loading="lazy"
        />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 text-center relative">
          <SectionLabel number="08" align="center">
            Contact The Company
          </SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95]">
            For partnership, sponsorship, press,
            <br />
            <span className="gold-text">and strategic inquiries.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-6 leading-relaxed max-w-2xl mx-auto">
            All commitments on behalf of the company must be confirmed in
            writing by R. Dale Brunet. The fastest path to a real conversation
            is the channels below.
          </p>

          <div
            data-testid="mg-contact-card"
            className="card-dark p-8 mt-10 max-w-2xl mx-auto"
          >
            <ContactRow label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} />
            <div className="gold-line my-4" />
            <ContactRow label="Phone" value={BRAND.phone} href={`tel:${BRAND.phoneTel}`} />
            <div className="gold-line my-4" />
            <ContactRow label="Web" value={BRAND.website} href={BRAND.websiteUrl} />
            <div className="gold-line my-4" />
            <ContactRow label="Headquarters" value={BRAND.city} />
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/sponsor"
              data-testid="mg-sponsor-cta"
              className="btn-gold"
            >
              Sponsor The Next Stage
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="mg-contact-cta"
              className="btn-outline"
            >
              General Inquiry
            </Link>
          </div>

          <p className="font-manrope text-muted-ink text-xs mt-12 max-w-2xl mx-auto leading-relaxed">
            The Next Stage Media Group, LLC is a for-profit company.
            Sponsorship commitments and other support are business engagements
            and are not tax-deductible charitable contributions.
          </p>
        </div>
      </section>
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div className="card-dark p-4">
      <div className="font-bebas tracking-[0.22em] text-gold text-[11px] uppercase">
        {label}
      </div>
      <div className="font-manrope text-white text-sm mt-1.5">{children}</div>
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="card-dark p-7">
      <Icon className="text-gold" size={28} strokeWidth={1.6} />
      <h3 className="font-shoulders font-black uppercase tracking-wider text-xl text-white mt-5">
        {title}
      </h3>
      <p className="font-manrope text-soft-gray text-[15px] mt-3 leading-relaxed">
        {body}
      </p>
    </div>
  );
}

function ArchCard({ kicker, title, body, icon: Icon, featured }) {
  return (
    <div
      className={`card-dark p-7 flex flex-col ${
        featured ? "corner-bracket border-gold" : ""
      }`}
    >
      <Icon
        className={featured ? "text-gold-bright" : "text-gold"}
        size={28}
        strokeWidth={1.6}
      />
      <div className="font-bebas tracking-[0.22em] text-gold text-[11px] uppercase mt-5">
        {kicker}
      </div>
      <h3 className="font-bebas text-white text-2xl leading-tight mt-2">
        {title}
      </h3>
      <p className="font-manrope text-soft-gray text-[15px] mt-3 leading-relaxed flex-1">
        {body}
      </p>
    </div>
  );
}

function Role({ num, title, body }) {
  return (
    <div className="card-dark p-6">
      <div className="font-shoulders font-black text-3xl text-gold-deep">
        {num}
      </div>
      <h3 className="font-shoulders font-black uppercase tracking-wider text-lg text-white mt-3">
        {title}
      </h3>
      <p className="font-manrope text-soft-gray text-sm mt-2 leading-relaxed">
        {body}
      </p>
    </div>
  );
}

function ContactRow({ label, value, href }) {
  const content = (
    <>
      <span className="font-bebas tracking-[0.22em] text-gold text-[11px] uppercase shrink-0 w-24 text-left">
        {label}
      </span>
      <span className="font-manrope text-white text-base text-left flex-1">
        {value}
      </span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        className="flex items-center gap-4 hover:text-gold-bright transition group"
      >
        {content}
        <Sparkles
          size={14}
          className="text-muted-ink group-hover:text-gold-bright transition shrink-0"
        />
      </a>
    );
  }
  return <div className="flex items-center gap-4">{content}</div>;
}
